import {FC, useMemo} from 'react';

import {Field, useFormikContext} from 'formik';
import {useTranslation} from 'react-i18next';

import {Box, Stack, Typography} from '@mui/material';

import {EmpionDatePicker} from '../EmpionDatePicker/EmpionDatePicker';
import {EmpionTextField} from '../EmpionTextField/EmpionTextField';

import {FormCheckbox} from './Checkbox';
import {LanguageInput} from './LanguageInput';
import {FormRadio} from './Radio';
import FormSelect from './Select';
import {SkillInput} from './SkillInput';
import {TrackRecordInput} from './TrackRecordInput';
import {FormSection as FormItemOptions} from './types';
import {empionGreen} from '../../layouts/theme/EmpionColors';

const getTestId = (d: {name: string}) => {
  return d.name + '-input';
};

const FormItem: FC<{
  opts: FormItemOptions;
  errors: any; // TODO: type this
  touched: any; // TODO: type this
}> = ({opts: section, errors, touched}) => {
  // TODO: Consider adding 'htmlFor' to the label
  const {values, setFieldValue} = useFormikContext();

  const {t} = useTranslation();
  const {hideCondition} = section;
  const isHidden = useMemo(() => {
    return hideCondition ? hideCondition(values) : false;
  }, [hideCondition, values]);
  if (isHidden) {
    return null;
  }
  const Label: FC<{
    label?: string | null;
  }> = (props) => {
    if (!props.label) {
      return null;
    }
    return (
      <Typography mt={2} sx={{color: empionGreen[700]}}>
        {props.label}
      </Typography>
    );
  };
  switch (section.type) {
    case 'title':
      return (
        <Box key={section.data.title} sx={{my: 2}}>
          <Typography variant='h2' sx={{color: empionGreen[700]}}>
            {section.data.title}
          </Typography>
        </Box>
      );
    case 'subtitle':
      return (
        <Box key={section.data.subtitle} sx={{mt: 3, mb: 0}}>
          <Typography variant='h4' sx={{color: empionGreen[700]}}>
            {section.data.subtitle}
          </Typography>
        </Box>
      );
    case 'label':
      return (
        <Box key={section.data.label} sx={{}}>
          <Typography mt={2} sx={{color: empionGreen[700]}}>
            {section.data.label}
          </Typography>
        </Box>
      );
    case 'text':
    case 'textarea': {
      const error = errors[section.data.name as keyof typeof errors];
      const isTouched = touched[section.data.name as keyof typeof touched];
      return (
        <Box key={section.data.name} sx={{}}>
          <Label {...section.data} />
          <Field
            name={section.data.name}
            size='small'
            fullWidth
            multiline={section.type === 'textarea'}
            rows={section.type === 'textarea' ? 4 : 1}
            helperText={error && isTouched ? error : ''}
            placeholder={section.data.placeholder}
            error={error && isTouched ? true : false}
            type={section.type}
            component={EmpionTextField}
            sx={{my: 1}}
            inputProps={{
              'data-testid': getTestId(section.data),
            }}
          />
        </Box>
      );
    }
    case 'number': {
      const error = errors[section.data.name as keyof typeof errors];
      const isTouched = touched[section.data.name as keyof typeof touched];
      return (
        <Box key={section.data.name} sx={{}}>
          <Label {...section.data} />
          <Field
            name={section.data.name}
            size='small'
            fullWidth
            helperText={error && isTouched ? error : ''}
            placeholder={section.data.placeholder}
            error={error && isTouched ? true : false}
            type={section.type}
            component={EmpionTextField}
            sx={{my: 1}}
            inputProps={{
              'data-testid': getTestId(section.data),
            }}
          />
        </Box>
      );
    }
    case 'radio': {
      return (
        <Box key={section.data.name} sx={{my: 2}}>
          <FormRadio
            name={section.data.name}
            options={
              section.data.options.map((option) => ({
                label: option.label,
                value: option.id,
              })) || []
            }
            data-testid={getTestId(section.data)}
          />
        </Box>
      );
    }
    case 'checkbox': {
      return (
        <Box key={section.data.name} sx={{my: 2}}>
          <FormCheckbox data-testid={getTestId(section.data)} name={section.data.name} label={section.data.label} />
        </Box>
      );
    }
    case 'select': {
      const error = errors[section.data.name as keyof typeof errors];
      const isTouched = touched[section.data.name as keyof typeof touched];
      return (
        <Stack sx={{flex: 1}}>
          <Label {...section.data} />
          <FormSelect
            name={section.data.name}
            aria-label={section.data.label || section.data.name}
            size='small'
            sx={{my: 1}}
            options={section.data.options.map((option) => ({
              label: option.label,
              value: option.id,
            }))}
            helperText={error && isTouched ? error : ''}
            placeholder={section.data.placeholder || ''}
            error={error && isTouched ? true : false}
            data-testid={getTestId(section.data)}
            disabled={section.data.disabled}
            inputProps={{
              'data-testid': getTestId(section.data) + '-child',
            }}
          />
        </Stack>
      );
    }
    case 'stacked': {
      return (
        <Stack direction={'row'} spacing={2}>
          {section.data.sections.map((section, idx) => (
            <FormItem key={idx} opts={section} errors={errors} touched={touched} />
          ))}
        </Stack>
      );
    }
    case 'skills': {
      return (
        <Box sx={{my: 2}}>
          <SkillInput name={section.data.name} />
        </Box>
      );
    }
    case 'track-record': {
      return (
        <Box sx={{my: 2}}>
          <TrackRecordInput name={section.data.name} options={section.data.options} />
        </Box>
      );
    }
    case 'languages': {
      return (
        <Box sx={{my: 2}}>
          <LanguageInput name={section.data.name} />
        </Box>
      );
    }
    case 'earliest-start-date': {
      return (
        <Box sx={{mt: 8}}>
          <EmpionDatePicker
            name={section.data.name}
            label={t('applicantProfile.earliestStartDate.title') || ''}
            value={section.data.initialValue}
            setFieldValue={setFieldValue}
          />
        </Box>
      );
    }
    default:
      console.error('Unknown form item type', section);
      return null;
  }
};

export default FormItem;
